import { useSessionUser } from "hooks/useSession";
import { useContext, useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateInput } from "components/common/input";
import { WeekDayCheckboxGroup } from "components/common/checkbox/weekday/WeekDayCheckboxGroup";
import { CheckboxColor, SubscriptionType } from "store/types/enum";
import FormGroupLabel from "components/common/text/FormGroupLabel";
import { UseMutationResult } from "@tanstack/react-query";
import { formatBEDateToLongDateString, isLockedDaybreak, shortDayFormat } from "utils";
import { DefaultScheduleWorkingDays, ENVS, WeekDays } from "config";
import { Checkbox } from "components/common/checkbox";
import { StripboardContext } from "components/dashboard/body/projects/panels/schedule/stripboard/StripboardContext";
import type { StripboardTableDayBreak } from "components/dashboard/body/projects/panels/schedule/types";
import dayjs from "dayjs";
import { MdLock } from "react-icons/md";
import { Stars } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { sessionKeys } from "queries/profile.keys";
import { AxiosResponse } from "axios";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { LuChevronsUpDown } from "react-icons/lu";
import { useStripboardMutations } from "hooks/useStripboard";
import { useProjectsApi } from "hooks/useProjects";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { getScheduleRerunAvailability } from "utils/aiCreditsAvailability";
import {
  areScheduleBudgetRunPrerequisitesMet,
  getScheduleBudgetRunPrerequisiteMessage,
} from "utils/scheduleBudgetRunPrerequisites";
import { handleToast } from "utils/toasts";
import { ScheduleLimitField } from "../schedule-dialog";
import { reRunDayBreaksSchema } from "./ReRunDayBreaksModal.schema";
import type { ReRunDayBreaksFormValues } from "./ReRunDayBreaksModal.types";

interface ReRunDayBreaksModalProps {
  open: boolean;
  setOpen: () => void;
  projectId: string;
  documentId: string;
  reloadStripboard?:
    | UseMutationResult<AxiosResponse<unknown, unknown>, unknown, void, unknown>
    | (() => Promise<unknown>);
  onUnlicensedScheduleBlocked?: () => void;
  daybreaksOverride?: StripboardTableDayBreak[];
  stripboardRowCountOverride?: number;
  scriptHasMinimalRunContent?: boolean;
  onPipelineRunRequested?: () => void;
  scheduleMergePayload?: { merge_policy?: string };
}

export const ReRunDayBreaksModal: React.FC<ReRunDayBreaksModalProps> = ({
  open,
  setOpen,
  projectId,
  documentId,
  reloadStripboard,
  onUnlicensedScheduleBlocked,
  daybreaksOverride,
  stripboardRowCountOverride,
  scheduleMergePayload,
  onPipelineRunRequested,
}) => {
  const queryClient = useQueryClient();
  const { session: profile, isOneTimePurchase } = useSessionUser();
  const pricing_plan = profile?.pricing_plan;
  const isFreePlan = profile?.pricing_plan_id === Number(ENVS.freePlanId);
  const isStudioPlan = pricing_plan?.plan_name === SubscriptionType.STUDIO;

  const { useProject } = useProjectsApi({});
  const { data: project } = useProject(projectId);
  const isTrialProject = project?.isTrial;

  const stripboardContext = useContext(StripboardContext);
  const daybreaks =
    daybreaksOverride ??
    (Array.isArray(stripboardContext?.daybreaks) ? stripboardContext.daybreaks : []);
  const firstNonLockedDay = daybreaks.find((x) => !x.locked)?.date;
  const defaultStartDate = firstNonLockedDay ?? dayjs().format(shortDayFormat);
  const lockedCount = daybreaks.filter(isLockedDaybreak).length;

  const {
    control,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<ReRunDayBreaksFormValues>({
    resolver: yupResolver(reRunDayBreaksSchema),
    mode: "onChange",
    defaultValues: {
      schedule_start_date: defaultStartDate,
      timeMode: "hours",
      hours: 8,
      maxDays: 5,
      working_days: [...DefaultScheduleWorkingDays],
      exclude_national_holidays: true,
    },
  });

  const timeMode = watch("timeMode");
  const maxDays = watch("maxDays");
  const hours = watch("hours");

  useEffect(() => {
    if (!open) return;
    setValue("schedule_start_date", defaultStartDate);
  }, [open, defaultStartDate, setValue]);

  const { embeddedProjectLicense } = useProjectAuth(projectId);
  const scheduleRerunBlockedByLicense = embeddedProjectLicense.isLicensed === false;

  const onScheduleLicenseBlocked = useCallback(() => {
    onUnlicensedScheduleBlocked?.();
  }, [onUnlicensedScheduleBlocked]);

  const { reRunScheduling } = useStripboardMutations({
    projectId,
    documentId,
    scheduleRerunGuard:
      scheduleRerunBlockedByLicense && onUnlicensedScheduleBlocked
        ? { blocked: true, onBlocked: onScheduleLicenseBlocked }
        : scheduleRerunBlockedByLicense
          ? { blocked: true, onBlocked: () => {} }
          : undefined,
  });

  const canRunAiReschedule = areScheduleBudgetRunPrerequisitesMet(project ?? undefined);
  const schedulePrerequisiteMessage = getScheduleBudgetRunPrerequisiteMessage(project ?? undefined);

  const scheduleAvail = getScheduleRerunAvailability(project ?? undefined);
  const scheduleRerunsLeft = scheduleAvail.remainingUnits;
  const rerunsExhausted = scheduleAvail.exhausted;

  const submitDisabled =
    !canRunAiReschedule ||
    isTrialProject ||
    scheduleRerunBlockedByLicense ||
    (!isOneTimePurchase && (rerunsExhausted || isFreePlan));

  const handleSuccess = () => {
    setOpen();
    queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    if (typeof reloadStripboard === "function") {
      void reloadStripboard();
    }
  };

  const onSubmit = (values: ReRunDayBreaksFormValues) => {
    if (!canRunAiReschedule) {
      handleToast(
        schedulePrerequisiteMessage ??
          "Finish script breakdown before running schedule or budget AI.",
        "error"
      );
      return;
    }
    if (isTrialProject) return;
    if (isFreePlan && !isOneTimePurchase) return;

    if (timeMode === "days") {
      if (maxDays <= lockedCount) {
        setError("maxDays", {
          type: "manual",
          message: '"Max total days" should be greater than the number of locked days',
        });
        return;
      }
      const stripboardLength =
        typeof stripboardRowCountOverride === "number"
          ? stripboardRowCountOverride
          : (() => {
              if (
                reloadStripboard &&
                typeof reloadStripboard === "object" &&
                "data" in reloadStripboard
              ) {
                const raw = (reloadStripboard as { data?: { data?: unknown[] } }).data?.data;
                return Array.isArray(raw) ? raw.length : 0;
              }
              return 0;
            })();
      if (stripboardLength / maxDays > 50) {
        setError("maxDays", {
          type: "manual",
          message: '"Max total days" is too low to create a schedule',
        });
        return;
      }
    }

    const exclude_weekdays = WeekDays.filter((d) => !values.working_days.includes(d));

    onPipelineRunRequested?.();

    reRunScheduling.mutate(
      {
        max_days: timeMode === "days" ? values.maxDays : undefined,
        max_hours_per_day: timeMode === "hours" ? values.hours : undefined,
        schedule_start_date: values.schedule_start_date.trim(),
        exclude_national_holidays: values.exclude_national_holidays,
        exclude_weekdays,
        ...(scheduleMergePayload ?? {}),
      },
      { onSuccess: handleSuccess }
    );
  };

  return (
    <Modal layout="panel" isOpen={open} onClose={setOpen} maxWidth="max-w-lg">
      <ModalHeader
        layout="panel"
        title="AI Reschedule"
        icon={<Stars />}
        subtitle="Redistribute scenes from start date."
        onClose={setOpen}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody layout="panel" showBorders={false}>
          <div className="flex flex-col gap-4">
            {schedulePrerequisiteMessage ? (
              <p className="m-0 text-xs text-primary" role="status">
                {schedulePrerequisiteMessage}
              </p>
            ) : null}

            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className="w-fit">
                <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
                  <MdLock size={14} />
                  <span>{lockedCount ? "See" : "No"} locked shoot days</span>
                  <LuChevronsUpDown size={14} />
                </div>
              </DropdownMenuTrigger>
              {lockedCount > 0 ? (
                <DropdownMenuContent>
                  {daybreaks
                    .reduce((acc: string[], daybreak, idx) => {
                      if (daybreak.locked) {
                        acc.push(`Day ${idx + 1} - ${formatBEDateToLongDateString(daybreak.date)}`);
                      }
                      return acc;
                    }, [])
                    .map((day, idx) => (
                      <DropdownMenuItem key={idx}>{day}</DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              ) : null}
            </DropdownMenu>

            <div>
              <FormGroupLabel marginBottom>Start date</FormGroupLabel>
              <div className="card card-border rounded-xl p-0">
                <Controller
                  name="schedule_start_date"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      name="start_date"
                      format="MMM d, yyyy"
                      handleChange={(date) => field.onChange(date ?? "")}
                      value={field.value || null}
                      normal
                      minDate={new Date()}
                      placeholder="Start date"
                      disabledDates={daybreaks.filter(isLockedDaybreak).map((day) => ({
                        startDate: new Date(day.date),
                        endDate: new Date(day.date),
                      }))}
                    />
                  )}
                />
              </div>
              {errors.schedule_start_date ? (
                <FormGroupLabel className="mt-2" noUpperCase warningLabel>
                  {errors.schedule_start_date.message}
                </FormGroupLabel>
              ) : null}
            </div>

            <ScheduleLimitField
              mode={timeMode}
              onModeChange={(mode) => setValue("timeMode", mode)}
              hours={hours}
              onHoursChange={(value) => setValue("hours", value, { shouldValidate: true })}
              maxDays={maxDays}
              onMaxDaysChange={(value) => setValue("maxDays", value, { shouldValidate: true })}
              hoursError={timeMode === "hours" ? errors.hours?.message : undefined}
              maxDaysError={timeMode === "days" ? errors.maxDays?.message : undefined}
            />

            <Controller
              name="exclude_national_holidays"
              control={control}
              render={({ field }) => (
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    name="exclude-holidays-checkbox"
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    checkboxColor={CheckboxColor.Gray}
                  />
                  <FormGroupLabel marginLeft>Skip US national holidays</FormGroupLabel>
                </label>
              )}
            />

            <Controller
              name="working_days"
              control={control}
              render={({ field }) => (
                <div>
                  <FormGroupLabel marginBottom>Working days</FormGroupLabel>
                  <WeekDayCheckboxGroup
                    checked={field.value}
                    onChange={field.onChange}
                    checkboxColor={CheckboxColor.Gray}
                  />
                  {errors.working_days ? (
                    <FormGroupLabel className="mt-2" noUpperCase warningLabel>
                      {errors.working_days.message}
                    </FormGroupLabel>
                  ) : null}
                </div>
              )}
            />
          </div>
        </ModalBody>
        <ModalFooter layout="panel">
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full justify-end gap-2">
              <Button size="lg" variant="ghostSecondary" onClick={setOpen} type="button">
                Cancel
              </Button>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    type="submit"
                    size="lg"
                    variant="ghostBrand"
                    isLoading={reRunScheduling.isPending}
                    disabled={submitDisabled}
                  >
                    {(rerunsExhausted && !isOneTimePurchase) ||
                    isTrialProject ||
                    scheduleRerunBlockedByLicense ? (
                      <MdLock />
                    ) : (
                      <Stars className="h-5 w-5" />
                    )}
                    AI Reschedule
                  </Button>
                </TooltipTrigger>
                {isTrialProject ? (
                  <TooltipContent>
                    Rescheduling is not available for trial projects. Purchase a license to unlock
                    this feature.
                  </TooltipContent>
                ) : scheduleRerunBlockedByLicense ? (
                  <TooltipContent>
                    Purchase a project license to run AI schedule and reschedule.
                  </TooltipContent>
                ) : schedulePrerequisiteMessage ? (
                  <TooltipContent>{schedulePrerequisiteMessage}</TooltipContent>
                ) : null}
              </Tooltip>
            </div>
            {isTrialProject ? (
              <FormGroupLabel className="mt-1" noUpperCase warningLabel>
                Rescheduling is not available for trial projects. Purchase a license to unlock this
                feature.
              </FormGroupLabel>
            ) : scheduleRerunBlockedByLicense ? (
              <FormGroupLabel className="mt-1" noUpperCase warningLabel>
                Purchase a project license to run AI schedule and reschedule.
              </FormGroupLabel>
            ) : !isOneTimePurchase ? (
              <FormGroupLabel
                className="mt-1"
                noUpperCase
                warningLabel={rerunsExhausted || isFreePlan}
              >
                {isFreePlan
                  ? "Upgrade to access this feature"
                  : !isStudioPlan
                    ? `AI credits: ${scheduleRerunsLeft} remaining (${scheduleAvail.cost} per schedule rerun). Upgrade for more capacity.`
                    : ""}
              </FormGroupLabel>
            ) : null}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ReRunDayBreaksModal;
